import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  NetworkCheck,
  Computer,
  Wifi,
  Storage,
  CheckCircle,
  Error,
  AccessTime,
  NetworkPing
} from '@mui/icons-material';
import api from '../../services/api';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function TroubleshootingSimple() {
  const [activeTab, setActiveTab] = useState(0);
  const [interfaces, setInterfaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  // Estados dos formulários
  const [targetHost, setTargetHost] = useState('');
  const [targetPort, setTargetPort] = useState(80);
  const [maxHops, setMaxHops] = useState(30);
  const [timeout, setTimeout] = useState(5);
  const [count, setCount] = useState(4);
  const [sourceInterface, setSourceInterface] = useState('');

  // Carregar interfaces
  useEffect(() => {
    const loadInterfaces = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await api.get('/admin/troubleshooting/network/interfaces');
        if (response.data?.data?.interfaces) {
          setInterfaces(response.data.data.interfaces);
        }
      } catch (err) {
        console.error('Erro ao carregar interfaces:', err);
      }
    };
    loadInterfaces();
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setResults(null);
    setError('');
  };

  const executeTest = async (testType, data) => {
    setLoading(true);
    setError('');
    setResults(null);

    try {
      const response = await api.post(`/admin/troubleshooting/network/${testType}`, data);
      if (response.data?.success) {
        setResults({
          success: response.data.success,
          data: response.data.data,
        });
      } else {
        setError('Erro no teste');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao executar teste');
    } finally {
      setLoading(false);
    }
  };

  const renderResult = (result) => {
    if (!result) return null;

    const isSuccess = result.success && !result.data.error_message;
    const Icon = isSuccess ? CheckCircle : Error;
    const color = isSuccess ? 'success' : 'error';

    return (
      <Card sx={{ mt: 2, bgcolor: '#ffffff', boxShadow: 2, border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Icon color={color} sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ color: '#000000' }}>Resultado do Teste</Typography>
            <Chip 
              label={isSuccess ? 'SUCESSO' : 'FALHOU'} 
              color={color} 
              size="small" 
              sx={{ ml: 2 }}
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                <strong>Host:</strong> {result.data.target_host}
              </Typography>
              {result.data.target_port && (
                <Typography variant="body2">
                  <strong>Porta:</strong> {result.data.target_port}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {result.data.response_time && (
                <Typography variant="body2">
                  <AccessTime fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  <strong>Tempo:</strong> {result.data.response_time}ms
                </Typography>
              )}
              {result.data.packet_loss !== undefined && (
                <Typography variant="body2">
                  <strong>Perda:</strong> {result.data.packet_loss}%
                </Typography>
              )}
            </Grid>
          </Grid>
          {result.data.hops && (
            <Box mt={2}>
              <Typography variant="body2" fontWeight="bold">Saltos:</Typography>
              <Paper sx={{ p: 1, mt: 1, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" component="pre" sx={{ fontSize: '0.8rem', color: '#000000' }}>
                  {result.data.hops.map((hop, index) => (
                    <div key={index}>
                      <strong>Salto {hop.hop}:</strong> {hop.info} 
                    </div>
                  ))}
                </Typography>
              </Paper>
            </Box>
          )}
          {result.data.total_time && (
            <Box mt={2}>
              <Typography variant="body2" fontWeight="bold">Tempo Total:</Typography>
              <Paper sx={{ p: 1, mt: 1, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#000000' }}>
                  {result.data.total_time}ms
                </Typography>
              </Paper>
            </Box>
          )}
          {result.data.output && (
            <Box mt={2}>
              <Typography variant="body2" fontWeight="bold">Saída:</Typography>
              <Paper sx={{ p: 1, mt: 1, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" component="pre" sx={{ fontSize: '0.8rem', color: '#000000' }}>
                  {result.data.output}
                </Typography>
              </Paper>
            </Box>
          )}

          {result.data.error_message && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {result.data.error_message}
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderComprehensiveResults = (results) => {
    if (!results) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Card sx={{ bgcolor: '#ffffff', boxShadow: 2, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#000000' }}>
              Teste Completo - {results.target_host}
            </Typography>
            <Box display="flex" gap={2} mb={2}>
              <Chip label={`${results?.summary?.passed} OK`} color="success" size="small" />
              <Chip label={`${results?.summary?.failed} Falhou`} color="error" size="small" />
              <Chip label={`${results?.summary?.total} Total`} color="primary" size="small" />
            </Box>

            {results?.tests?.map((test, index) => (
              <Box key={index} sx={{ 
                mb: 2, 
                p: 2, 
                border: '1px solid #e0e0e0', 
                borderRadius: 1, 
                bgcolor: '#fafafa' 
              }}>
                <Box display="flex" alignItems="center" mb={1}>
                  {test.result.success ? (
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                  ) : (
                    <Error color="error" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="h6" sx={{ color: '#000000' }}>{test.name.toUpperCase()}</Typography>
                  {test.result.response_time && (
                    <Typography variant="body2" sx={{ color: '#666666', ml: 'auto' }}>
                      {test.result.response_time}ms
                    </Typography>
                  )}
                </Box>
                {test.result.error_message && (
                  <Typography variant="body2" color="error">
                    Erro: {test.result.error_message}
                  </Typography>
                )}
              </Box>
            ))}
          </CardContent>
        </Card>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#ffffff', minHeight: '100vh' }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#000000' }}>
        <NetworkCheck sx={{ verticalAlign: 'middle', mr: 1 }} />
        Troubleshooting de Rede
      </Typography>

      <Tabs 
        value={activeTab} 
        onChange={handleTabChange}
        sx={{ 
          borderBottom: 1, 
          borderColor: '#e0e0e0',
          bgcolor: '#ffffff',
          '& .MuiTab-root': {
            color: '#000000'
          }
        }}
      >
        <Tab label="Ping" icon={<NetworkPing />} />
        <Tab label="Porta" icon={<Storage />} />
        <Tab label="Tracert" icon={<Computer />} />
        {/* <Tab label="Telnet" icon={<Wifi />} />
        <Tab label="Completo" icon={<NetworkCheck />} /> */}
      </Tabs>

      {/* Ping */}
      <TabPanel value={activeTab} index={0}>
        <Card sx={{ bgcolor: '#ffffff', boxShadow: 2, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#000000' }}>Teste de Ping</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Host"
                  value={targetHost}
                  onChange={(e) => setTargetHost(e.target.value)}
                  placeholder="8.8.8.8"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Pacotes"
                  value={count}
                  onChange={(e) => setCount(parseInt(e.target.value))}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Timeout (s)"
                  value={timeout}
                  onChange={(e) => setTimeout(parseInt(e.target.value))}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Interface (Opcional)</InputLabel>
                  <Select value={sourceInterface} onChange={(e) => setSourceInterface(e.target.value)}>
                    <MenuItem value="">Automática</MenuItem>
                    {interfaces?.map((iface) => (
                      <MenuItem key={iface} value={iface}>{iface}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={() => executeTest('ping', { target_host: targetHost, count, timeout, source_interface: sourceInterface })}
                  disabled={loading || !targetHost}
                  startIcon={loading ? <CircularProgress size={20} /> : <NetworkPing />}
                >
                  {loading ? 'Executando...' : 'Ping'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        {renderResult(results)}
      </TabPanel>

      {/* Porta */}
      <TabPanel value={activeTab} index={1}>
        <Card sx={{ bgcolor: '#ffffff', boxShadow: 2, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#000000' }}>Teste de Porta</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Host"
                  value={targetHost}
                  onChange={(e) => setTargetHost(e.target.value)}
                  placeholder="192.168.1.1"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Porta"
                  value={targetPort}
                  onChange={(e) => setTargetPort(parseInt(e.target.value))}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Timeout (s)"
                  value={timeout}
                  onChange={(e) => setTimeout(parseInt(e.target.value))}
                />
              </Grid>
               <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Interface (Opcional)</InputLabel>
                  <Select value={sourceInterface} onChange={(e) => setSourceInterface(e.target.value)}>
                    <MenuItem value="">Automática</MenuItem>
                    {interfaces?.map((iface) => (
                      <MenuItem key={iface} value={iface}>{iface}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={() => executeTest('port', { target_host: targetHost, target_port: targetPort, timeout, source_interface: sourceInterface })}
                  disabled={loading || !targetHost}
                  startIcon={loading ? <CircularProgress size={20} /> : <Storage />}
                >
                  {loading ? 'Testando...' : 'Testar Porta'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        {renderResult(results)}
      </TabPanel>

      {/* TRACERT */}
      <TabPanel value={activeTab} index={2}>
        <Card sx={{ bgcolor: '#ffffff', boxShadow: 2, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#000000' }}>Teste Tracert</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Host"
                  value={targetHost}
                  onChange={(e) => setTargetHost(e.target.value)}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Máximo de Saltos"
                  value={maxHops}
                  onChange={(e) => setMaxHops(parseInt(e.target.value))}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Timeout (s)"
                  value={timeout}
                  onChange={(e) => setTimeout(parseInt(e.target.value))}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Interface (Opcional)</InputLabel>
                  <Select value={sourceInterface} onChange={(e) => setSourceInterface(e.target.value)}>
                    <MenuItem value="">Automática</MenuItem>
                    {interfaces?.map((iface) => (
                      <MenuItem key={iface} value={iface}>{iface}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={() => executeTest('tracert', { target_host: targetHost, target_port: targetPort, timeout, source_interface: sourceInterface })}
                  disabled={loading || !targetHost}
                  startIcon={loading ? <CircularProgress size={20} /> : <Computer />}
                >
                  {loading ? 'Conectando...' : 'Testar Tracert'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        {renderResult(results)}
      </TabPanel>

      {/* Telnet */}
      <TabPanel value={activeTab} index={3}>
        <Card sx={{ bgcolor: '#ffffff', boxShadow: 2, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#000000' }}>Teste Telnet</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Host"
                  value={targetHost}
                  onChange={(e) => setTargetHost(e.target.value)}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Porta"
                  value={targetPort}
                  onChange={(e) => setTargetPort(parseInt(e.target.value))}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Timeout (s)"
                  value={timeout}
                  onChange={(e) => setTimeout(parseInt(e.target.value))}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={() => executeTest('telnet', { target_host: targetHost, target_port: targetPort, timeout, source_interface: sourceInterface })}
                  disabled={loading || !targetHost}
                  startIcon={loading ? <CircularProgress size={20} /> : <Wifi />}
                >
                  {loading ? 'Conectando...' : 'Testar Telnet'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        {renderResult(results)}
      </TabPanel>

      {/* Completo */}
      <TabPanel value={activeTab} index={4}>
        <Card sx={{ bgcolor: '#ffffff', boxShadow: 2, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#000000' }}>Teste Completo</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Host"
                  value={targetHost}
                  onChange={(e) => setTargetHost(e.target.value)}
                  placeholder="google.com"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Timeout (s)"
                  value={timeout}
                  onChange={(e) => setTimeout(parseInt(e.target.value))}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Interface (Opcional)</InputLabel>
                  <Select value={sourceInterface} onChange={(e) => setSourceInterface(e.target.value)}>
                    <MenuItem value="">Automática</MenuItem>
                    {interfaces?.map((iface) => (
                      <MenuItem key={iface} value={iface}>{iface}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  Executa: Ping, SSH:22, Telnet:23, Porta:80, Porta:443
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => executeTest('comprehensive', { target_host: targetHost, timeout, source_interface: sourceInterface })}
                  disabled={loading || !targetHost}
                  startIcon={loading ? <CircularProgress size={20} /> : <NetworkCheck />}
                >
                  {loading ? 'Executando...' : 'Teste Completo'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        {results && renderComprehensiveResults(results)}
      </TabPanel>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}